import { useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "utils/redux/hooks";
import { Button } from "app/shadcnComponents/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "app/shadcnComponents/ui/card";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { cn } from "app/lib/utils";
import {
  selectFileSyncLogs,
  approveQuarantinedSync,
  rejectQuarantinedSync,
  selectApproveQuarantineStatus,
  selectRejectQuarantineStatus,
} from "./slice";
import { FileSyncLog } from "./types";
import SimpleModal from "app/components/Modals/SimpleModal";
import { getS } from "utils/helperFunctions";

const FileSyncPendingReviewTab = () => {
  const dispatch = useAppDispatch();
  const allLogs = useAppSelector(selectFileSyncLogs);
  const approveStatus = useAppSelector(selectApproveQuarantineStatus);
  const rejectStatus = useAppSelector(selectRejectQuarantineStatus);

  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedLog, setSelectedLog] = useState<FileSyncLog | null>(null);
  const [rejectReason, setRejectReason] = useState("");

  const reviewedLogs = useMemo(
    () => allLogs.filter((log) => !!log.approved_by),
    [allLogs]
  );

  const currentQuarantine = useMemo(() => {
    return allLogs?.find((log) => log.status === "quarantined") || null;
  }, [allLogs]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const handleApprove = () => {
    if (selectedLog) {
      dispatch(approveQuarantinedSync(selectedLog.id));
      setShowApproveModal(false);
      setSelectedLog(null);
    }
  };

  const handleReject = () => {
    if (selectedLog) {
      dispatch(
        rejectQuarantinedSync({ logId: selectedLog.id, reason: rejectReason })
      );
      setShowRejectModal(false);
      setSelectedLog(null);
      setRejectReason("");
    }
  };

  const getPreviouslyReviewedCard = () => {
    if (reviewedLogs.length === 0) {
      return null;
    }
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Previously Reviewed</CardTitle>
        </CardHeader>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-[var(--charcoal-05)] border-b border-[var(--charcoal-10)]">
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-[var(--charcoal-60)]">
                  Date
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-[var(--charcoal-60)]">
                  Changes
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-[var(--charcoal-60)]">
                  Decision
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-[var(--charcoal-60)]">
                  Reviewed At
                </th>
              </tr>
            </thead>
            <tbody>
              {reviewedLogs.slice(0, 10).map((log) => (
                <tr
                  key={log.id}
                  className="border-b border-[var(--charcoal-10)]"
                >
                  <td className="px-4 py-4">
                    <div className="font-medium text-sm">
                      {formatDate(log.started_at)}
                    </div>
                  </td>
                  <td className="px-4 py-4 text-sm">
                    {(() => {
                      const changes = [];
                      if (log.users_to_add > 0)
                        changes.push(
                          `${log.users_to_add} user${
                            log.users_to_add === 1 ? "" : "s"
                          } added`
                        );
                      if (log.users_to_update > 0)
                        changes.push(
                          `${log.users_to_update} user${
                            log.users_to_update === 1 ? "" : "s"
                          } updated`
                        );
                      if (log.users_to_deactivate > 0)
                        changes.push(
                          `${log.users_to_deactivate} user${
                            log.users_to_deactivate === 1 ? "" : "s"
                          } deactivated`
                        );
                      if (log.users_to_reactivate)
                        changes.push(
                          `${log.users_to_reactivate} user${
                            log.users_to_reactivate === 1 ? "" : "s"
                          } reactivated`
                        );
                      return changes.length > 0
                        ? changes.join(", ")
                        : "No changes";
                    })()}
                  </td>
                  <td className="px-4 py-4">
                    <span
                      className={cn(
                        "inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-medium",
                        log.status === "rejected"
                          ? "bg-[var(--error-red-10)] text-[var(--error-red-100)]"
                          : "bg-[var(--success-green-20)] text-[var(--success-green-100)]"
                      )}
                    >
                      {log.status === "rejected" ? "Rejected" : "Approved"}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-sm text-[var(--charcoal-60)]">
                    {log.approved_at ? formatDate(log.approved_at) : "-"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    );
  };

  const getHighImpactCard = (currentQuarantine: FileSyncLog) => {
    if (
      currentQuarantine.users_to_deactivate === 0 &&
      !currentQuarantine.users_to_reactivate &&
      !currentQuarantine.details?.usersWithManagerChanges?.length
    ) {
      return null;
    }

    return (
      <Card className="mb-6">
        <CardHeader className="py-4">
          <CardTitle className="text-sm">
            High-Impact Changes Requiring Attention
          </CardTitle>
        </CardHeader>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-[var(--charcoal-05)] border-b border-[var(--charcoal-10)]">
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-[var(--charcoal-60)]">
                  Type
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-[var(--charcoal-60)]">
                  Details
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-[var(--charcoal-60)]">
                  Impact
                </th>
              </tr>
            </thead>
            <tbody>
              {currentQuarantine.users_to_deactivate > 0 && (
                <tr className="border-b border-[var(--charcoal-10)]">
                  <td className="px-4 py-4">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-medium bg-[var(--error-red-10)] text-[var(--error-red-100)]">
                      Deactivation
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <div className="font-medium">
                      {currentQuarantine.users_to_deactivate} user
                      {getS(currentQuarantine.users_to_deactivate)} will be
                      deactivated
                    </div>
                    <div className="text-sm text-[var(--charcoal-60)]">
                      {currentQuarantine.details?.usersToDeactivate
                        ?.slice(0, 3)
                        .map((u) => u.emailAddress)
                        .join(", ")}
                      {(currentQuarantine.details?.usersToDeactivate?.length ??
                        0) > 3 && " ..."}
                    </div>
                  </td>
                  <td className="px-4 py-4 text-[var(--error-red-100)] font-medium">
                    High
                  </td>
                </tr>
              )}
              {!!currentQuarantine.users_to_reactivate && (
                <tr className="border-b border-[var(--charcoal-10)]">
                  <td className="px-4 py-4">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-medium bg-teal-100 text-teal-700">
                      Reactivation
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <div className="font-medium">
                      {currentQuarantine.users_to_reactivate} user
                      {getS(currentQuarantine.users_to_reactivate)} will be
                      reactivated
                    </div>
                    <div className="text-sm text-[var(--charcoal-60)]">
                      {currentQuarantine.details?.usersToReactivate
                        ?.slice(0, 3)
                        .map((u) => u.emailAddress)
                        .join(", ")}
                      {(currentQuarantine.details?.usersToReactivate?.length ??
                        0) > 3 && " ..."}
                    </div>
                  </td>
                  <td className="px-4 py-4 text-teal-600 font-medium">
                    Medium
                  </td>
                </tr>
              )}
              {!!currentQuarantine.details?.usersWithManagerChanges?.length && (
                <tr className="border-b border-[var(--charcoal-10)]">
                  <td className="px-4 py-4">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-medium bg-amber-100 text-amber-700">
                      Manager Change
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <div className="font-medium">
                      {currentQuarantine.details?.usersWithManagerChanges
                        ?.length || 0}{" "}
                      manager relationship
                      {getS(
                        currentQuarantine.details?.usersWithManagerChanges
                          ?.length || 0
                      )}{" "}
                      changing
                    </div>
                    <div className="text-sm text-[var(--charcoal-60)]">
                      {currentQuarantine.details?.usersWithManagerChanges
                        ?.slice(0, 2)
                        .map((u) => u.emailAddress)
                        .join(", ")}
                    </div>
                  </td>
                  <td className="px-4 py-4 text-amber-600 font-medium">
                    Medium
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    );
  };

  if (!currentQuarantine) {
    return (
      <div className="space-y-6">
        {/* Empty state */}
        <Card className="border-dashed">
          <CardContent className="py-12">
            <div className="text-center">
              <div className="mx-auto w-12 h-12 rounded-full bg-[var(--success-green-20)] flex items-center justify-center mb-4">
                <FontAwesomeIcon
                  icon="check-circle"
                  className="text-[var(--success-green-100)] text-xl"
                />
              </div>
              <h3 className="text-lg font-semibold mb-2">No Pending Reviews</h3>
              <p className="text-[var(--charcoal-60)] max-w-md mx-auto">
                All syncs are up to date. Large changes that exceed your safety
                thresholds will appear here for review.
              </p>
            </div>
          </CardContent>
        </Card>

        {getPreviouslyReviewedCard()}
      </div>
    );
  }

  const totalChanges =
    currentQuarantine.users_to_add +
    currentQuarantine.users_to_update +
    currentQuarantine.users_to_deactivate +
    (currentQuarantine.users_to_reactivate ?? 0);

  return (
    <div className="space-y-6">
      {/* Quarantine Alert */}
      <div className="bg-[var(--error-red-10)] border border-[var(--error-red-100)] rounded-xl p-6">
        <div className="flex items-start gap-4 mb-6">
          <div className="w-12 h-12 rounded-full bg-[var(--error-red-100)] flex items-center justify-center shrink-0">
            <FontAwesomeIcon
              icon={["far", "triangle-exclamation"]}
              className="text-white text-xl"
            />
          </div>
          <div>
            <h3 className="text-lg font-bold text-[var(--error-red-100)] mb-1">
              Sync Requires Manual Approval
            </h3>
            <p className="text-[var(--charcoal-80)]">
              The sync from{" "}
              <strong>{formatDate(currentQuarantine.started_at)}</strong>{" "}
              exceeds your safety threshold and is waiting for review. This
              change would affect{" "}
              <strong>
                {totalChanges} users{" "}
                {currentQuarantine.percent_affected &&
                  ` (${currentQuarantine.percent_affected.toFixed(1)}%)`}
              </strong>{" "}
              of your organization.
            </p>
            {currentQuarantine.threshold_exceeded_reason && (
              <p className="text-sm text-[var(--charcoal-60)] mt-2">
                Reason: {currentQuarantine.threshold_exceeded_reason}
              </p>
            )}
          </div>
        </div>

        {/* Proposed Changes Summary */}
        <div className="bg-white rounded-lg p-4 mb-6">
          <h4 className="font-semibold mb-4 text-black">
            Proposed Changes Summary
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="text-center p-4 bg-[var(--charcoal-05)] rounded-lg">
              <div className="text-3xl font-bold text-[var(--success-green-100)] mb-1">
                +{currentQuarantine.users_to_add}
              </div>
              <div className="text-sm text-[var(--charcoal-60)]">New Users</div>
            </div>
            <div className="text-center p-4 bg-[var(--charcoal-05)] rounded-lg">
              <div className="text-3xl font-bold text-[var(--sapphire-100)] mb-1">
                {currentQuarantine.users_to_update}
              </div>
              <div className="text-sm text-[var(--charcoal-60)]">
                Field Updates
              </div>
            </div>
            <div className="text-center p-4 bg-[var(--charcoal-05)] rounded-lg">
              <div className="text-3xl font-bold text-[var(--error-red-100)] mb-1">
                -{currentQuarantine.users_to_deactivate}
              </div>
              <div className="text-sm text-[var(--charcoal-60)]">
                Deactivations
              </div>
            </div>
            <div className="text-center p-4 bg-[var(--charcoal-05)] rounded-lg">
              <div className="text-3xl font-bold text-teal-600 mb-1">
                {currentQuarantine.users_to_reactivate ?? 0}
              </div>
              <div className="text-sm text-[var(--charcoal-60)]">
                Reactivation
              </div>
            </div>
            <div className="text-center p-4 bg-[var(--charcoal-05)] rounded-lg">
              <div className="text-3xl font-bold text-amber-600 mb-1">
                {currentQuarantine.details?.usersWithManagerChanges?.length ||
                  0}
              </div>
              <div className="text-sm text-[var(--charcoal-60)]">
                Manager Changes
              </div>
            </div>
          </div>
        </div>

        {/* High-Impact Changes Table */}
        {getHighImpactCard(currentQuarantine)}

        {/* Action Buttons */}
        <div className="flex gap-3">
          <Button
            variant="primary"
            className="bg-[var(--success-green-100)] hover:bg-[var(--success-green-100)]/90"
            onClick={() => {
              setSelectedLog(currentQuarantine);
              setShowApproveModal(true);
            }}
            disabled={approveStatus === "loading"}
          >
            {approveStatus === "loading" ? (
              <FontAwesomeIcon icon="spinner" className="animate-spin" />
            ) : (
              <FontAwesomeIcon icon="check" />
            )}
            Approve & Apply Changes
          </Button>
          <Button
            variant="primary"
            className="bg-[var(--error-red-100)] hover:bg-[var(--error-red-100)]/90"
            onClick={() => {
              setSelectedLog(currentQuarantine);
              setShowRejectModal(true);
            }}
            disabled={rejectStatus === "loading"}
          >
            {rejectStatus === "loading" ? (
              <FontAwesomeIcon icon="spinner" className="animate-spin" />
            ) : (
              <FontAwesomeIcon icon="times" />
            )}
            Reject Sync
          </Button>
        </div>
      </div>

      {getPreviouslyReviewedCard()}

      {/* Approve Modal */}
      <SimpleModal
        show={showApproveModal}
        onHide={() => setShowApproveModal(false)}
        title="Approve Sync?"
        centered
      >
        <p className="text-[var(--charcoal-60)] mb-6">
          This will apply all {totalChanges} changes to your organization. This
          action cannot be undone.
        </p>
        <div className="flex gap-3 justify-end">
          <Button
            variant="secondary-gray"
            onClick={() => setShowApproveModal(false)}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            className="bg-[var(--success-green-100)] hover:bg-[var(--success-green-100)]/90"
            onClick={handleApprove}
          >
            Approve
          </Button>
        </div>
      </SimpleModal>
      <SimpleModal
        show={showRejectModal}
        onHide={() => setShowRejectModal(false)}
        title="Reject Sync?"
        centered
      >
        <p className="text-[var(--charcoal-60)]">
          This will discard the pending sync. The file will need to be
          re-uploaded to try again.
        </p>
        <div>
          <label
            className="block text-sm font-medium mb-2"
            htmlFor="reject-reason"
          >
            Reason (optional)
          </label>
          <textarea
            id="reject-reason"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Enter reason for rejection..."
            className="w-full px-3 py-2 border border-[var(--charcoal-20)] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[var(--sapphire-100)] resize-none"
            rows={3}
          />
        </div>
        <div className="flex gap-3 justify-end">
          <Button
            variant="secondary-gray"
            onClick={() => setShowRejectModal(false)}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            className="bg-[var(--error-red-100)] hover:bg-[var(--error-red-100)]/90"
            onClick={handleReject}
          >
            Reject
          </Button>
        </div>
      </SimpleModal>
    </div>
  );
};

export default FileSyncPendingReviewTab;
